import { Button, Checkbox, Flex, Text } from '@chakra-ui/react';
import { useResources } from 'datx-jsonapi-react';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CloseEmoji from '@/assets/icons/ic-close.svg';

import { useAnalytics } from '@/context/Analytics';
import { IToastType } from '@/enums/ToastType';
import useSession from '@/hooks/useSession';
import { useStores } from '@/hooks/useStores';
import { useToast } from '@/hooks/useToast';
import { queryLecturePlaylists } from '@/queries/lecture-playlist';
import { queryThinProfilesByUserId } from '@/queries/thin-profiles';
import { LecturePlaylist } from '@/resources/LecturePlaylist';
import { ProfilePlaylist } from '@/resources/ProfilePlaylist';
import { ThinProfile } from '@/resources/ThinProfile';

interface IPlaylistItemProps {
  lectureId: string;
  playlist: ProfilePlaylist;
  deletePlaylist: (playlistId: string) => void;
}

export const PlaylistItem: FC<IPlaylistItemProps> = ({ lectureId, playlist, deletePlaylist }) => {
  const { t } = useTranslation();
  const { store } = useStores();
  const analytics = useAnalytics();
  const toast = useToast();
  const { user } = useSession();
  const { data } = useResources<ThinProfile>(queryThinProfilesByUserId(user?.id));
  const { data: lecturePlaylists, mutate: lecturesPlaylistMutate } = useResources(
    queryLecturePlaylists(data?.[0]?.id, { lecture_id: lectureId })
  );
  const [stateChangeText, setStateChangeText] = useState(null);

  async function handleLecturePlaylistStateChange(checked: boolean, playlistId: string) {
    if (checked) handleAddLectureToPlaylist(playlistId);
    if (!checked) handleRemoveLectureFromPlaylist(playlistId);
  }

  async function handleAddLectureToPlaylist(playlistId: string) {
    try {
      await store.request(LecturePlaylist.type, 'POST', {
        data: {
          type: LecturePlaylist.type,
          attributes: {
            lecture_id: lectureId,
            playlist_id: playlistId,
          },
        },
      });

      setStateChangeText(t('newLecture.bookmarkModal.added'));
      lecturesPlaylistMutate();

      analytics.track('lecture_added_to_playlist', {
        lecture_id: lectureId,
        playlist_id: playlistId,
      });
    } catch (response) {
      toast({ status: IToastType.ERROR, title: response?.error?.[0]?.detail });
    }
  }

  async function handleRemoveLectureFromPlaylist(playlistId: string) {
    try {
      const lecturePlaylist = lecturePlaylists.find((lp) => lp.playlistId === playlistId);

      await store.request(`${LecturePlaylist.type}/${lecturePlaylist.id}`, 'DELETE', {
        data: {
          type: LecturePlaylist.type,
          attributes: {
            lecture_id: lectureId,
            playlist_id: playlistId,
          },
        },
      });

      setStateChangeText(t('newLecture.bookmarkModal.removed'));
      lecturesPlaylistMutate();

      analytics.track('lecture_removed_from_playlist', {
        lecture_id: lectureId,
        playlist_id: playlistId,
      });
    } catch (response) {
      toast({ status: IToastType.ERROR, title: response?.error?.[0]?.detail });
    }
  }

  function isDefaultChecked(playlistId: string) {
    return Boolean(lecturePlaylists?.find((lp) => lp.playlistId === playlistId));
  }

  return (
    <Flex mt={4} justifyContent="space-between" alignItems="center">
      <Checkbox
        pb="2px"
        defaultChecked={isDefaultChecked(playlist.id)}
        onChange={(e) => handleLecturePlaylistStateChange(e.target.checked, playlist.id)}
      >
        {playlist.name}
      </Checkbox>
      <Text textTransform="uppercase" pl="4" flex="1" fontSize="sm" fontWeight="semibold">
        {stateChangeText}
      </Text>
      <Button onClick={() => deletePlaylist(playlist.id)} size="sm" variant="unstyled">
        <CloseEmoji height="16px" width="16px" />
      </Button>
    </Flex>
  );
};
